import { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {errorMsg, userListToLabelEmailPhoneValue} from '../../../../../../../Apis/functions';
import { permissionObj } from '../../../../../../../Helpers/permission';
import routes from '../../../../../../../Routes/routes';
import { TABS_ATTENDANCE, ATTENDANCE_SUBVIEW_MODEL } from '../../../Model/Model';
import {
    GetUserDeductionPolicy,
    UpdateUserDeductionPolicy,
} from '../../../../../../../reduxToolkit/DeductionPolicy/DeductionPolicyApi';
import {
    DEDUCTION_POLICY_TAB,
    STEPS_ADD_USER_DEDUCTION_POLICY,
    USER_DEDUCTION_ENTRY_MODEL,
    USER_DEDUCTION_POLICY_MODEL
} from '../../DeductionPolicy/Model/Model';
import {PersonChipBlack} from "../../../../../../../components/buttons/IconButtons";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @Description Logic hook for the Update User Deduction Policy stepper.
 * Fetches the existing policy via GetUserDeductionPolicy(userId) on mount and
 * pre-populates deductions and excluded global rules. Submits via PUT.
 */
const UpdateUserDeductionPolicyApp = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;
    const updateRow = state?.updateRow;

    const DEV = useSelector((s) => s?.CommonsSlice?.development);
    const { permission } = useSelector((s) => s.authenticatioauthennSlice);
    const canUpdate = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_DEDUCTION_POLICY_USER_UPDATE);

    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [emptyFields, setEmptyFields] = useState(false);

    const [user, setUser] = useState([]);
    const [deductions, setDeductions] = useState([{ ...USER_DEDUCTION_ENTRY_MODEL, _key: Date.now() }]);
    const [excludedGlobalRules, setExcludedGlobalRules] = useState([]);
    const [searchElement, setSearchElement] = useState('');
    const [modified, setModified] = useState(false);
    const [selectForDelete, setSelectForDelete] = useState([]);

    useLayoutEffect(() => {
        const stepArray = Object.values(STEPS_ADD_USER_DEDUCTION_POLICY)
            .filter(s => s?.TRANS && typeof s.INDEX === 'number' && s.INDEX !== STEPS_ADD_USER_DEDUCTION_POLICY.SUMMARY.INDEX)
            .sort((a, b) => a.INDEX - b.INDEX)
            .map(s => t(s.TRANS));
        setSteps(stepArray);
    }, [t]);

    useEffect(() => {
        if (!updateRow?.userId) return;
        getData(updateRow?.userId)
    }, []);

    useEffect(() => {
        if(modified) getData(updateRow?.userId)
    }, [modified]);

    const getData = (id) => {
        dispatch(GetUserDeductionPolicy(id))
            .then(({ payload: { data: { data, success } } }) => {
                if (!success || !data) return;
                setUser(userListToLabelEmailPhoneValue([data], 'userId', true, PersonChipBlack));

                if (data.deductions?.length > 0) {
                    setDeductions(data.deductions.map(item => ({
                        ...USER_DEDUCTION_ENTRY_MODEL.set(item),
                        _key: `update-${item.id}`,
                    })));
                }
            });
    }

    const handleStepChange = (step) => setActiveStep(step);

    /**
     * Clears the data owned by a specific step.
     * @param {number} step - Step index.
     */
    const handleClearStep = (step) => {
        const clearMap = {
            [STEPS_ADD_USER_DEDUCTION_POLICY.SELECT_USER.INDEX]: () => setUser([]),
            [STEPS_ADD_USER_DEDUCTION_POLICY.DEDUCTION_POLICY.INDEX]: () =>
                setDeductions([{ ...USER_DEDUCTION_ENTRY_MODEL, _key: Date.now() }]),
            [STEPS_ADD_USER_DEDUCTION_POLICY.EXCLUDED_GLOBAL_DEDUCTIONS.INDEX]: () => setExcludedGlobalRules([]),
        };
        clearMap[step]?.();
        setEmptyFields(false);
    };

    /**
     * Builds the API body and submits the updated user deduction policy.
     */
    const handleSubmit = () => {
        if (user.length === 0 || deductions.filter(e => !e.isValid()).length > 0) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }
        if (deductions.some(d => d.isDuplicateName(deductions))) {
            setEmptyFields(true);
            toast.warn(t('payroll_deduction_user_item_duplicate'));
            return;
        }
        const body = {
            ...USER_DEDUCTION_POLICY_MODEL.toRegister(user, deductions, excludedGlobalRules),
            id: updateRow.id,
        };
        dispatch(UpdateUserDeductionPolicy(body)).then(({ payload: { data: { success, code } } }) => {
            if (success) {
                toast.success(t('has_been_updated_successfully'));
                setModified(true);
                setSearchElement('');
                setSelectForDelete([]);
                setExcludedGlobalRules([]);
            } else errorMsg(code);
        });
    };

    const handleOnBack = () => {
        navigate(routes.attendance.main, {
            state: {
                currentTab: TABS_ATTENDANCE.MANAGE.INDEX,
                currentSubView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.DEDUCTION_POLICY,
                currentSubViewChild: DEDUCTION_POLICY_TAB.USER.key,
            },
        });
    };

    return {
        t,
        canUpdate,
        steps,
        activeStep, setActiveStep,
        handleStepChange,
        emptyFields, setEmptyFields,
        handleClearStep,
        handleOnBack,
        user, setUser,
        deductions, setDeductions,
        excludedGlobalRules, setExcludedGlobalRules,
        selectForDelete, setSelectForDelete,
        searchElement, setSearchElement,
        modified, setModified,
        handleSubmit,
        id:updateRow?.userId
    };
};

export default UpdateUserDeductionPolicyApp;
